import { useEffect, useRef, useState, useCallback } from "react";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";
import type { paths } from "../../api/generated/api-types";

type TaskListResponse =
  paths["/tasks"]["get"]["responses"]["200"]["content"]["application/json"];

type SsePayload = TaskListResponse;

export type TaskListItem = TaskListResponse["items"][number];

export type TaskListAnchor = TaskListItem["anchors"][number];

export type TaskListPublication = NonNullable<TaskListItem["publication"]>;

type TaskListQuery = NonNullable<paths["/tasks"]["get"]["parameters"]["query"]>;
export type StatusFilterValue = NonNullable<TaskListQuery["statusFilter"]>;
export type TaskTypeValue = NonNullable<TaskListQuery["taskTypeFilter"]>;

export interface TaskListFilters {
  anchorTextFilter: string;
  targetUrlFilter: string;
  // undefined — фильтр по признаку деактивации не выставлен: в списке и
  // активные задачи, и деактивированные.
  deactivated: boolean | undefined;
  regionFilter: string[];
  // undefined — фильтр по статусу не выставлен: в списке задачи любого статуса.
  statusFilter: StatusFilterValue | undefined;
  // undefined — фильтр по типу задачи не выставлен: в списке задачи любого типа.
  taskTypeFilter: TaskTypeValue | undefined;
}

export const EMPTY_FILTERS: TaskListFilters = {
  anchorTextFilter: "",
  targetUrlFilter: "",
  deactivated: undefined,
  regionFilter: [],
  statusFilter: undefined,
  taskTypeFilter: undefined,
};

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: TaskListItem[]; totalCount: number }
  | { kind: "error" };

const DEFAULT_PAGE_SIZE = 25;

export interface UseTaskListSubscriptionResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

export function useTaskListSubscription(
  filters: TaskListFilters,
): UseTaskListSubscriptionResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const clientRef = useRef<SseClient | null>(null);
  const initialLoadDone = useRef(false);

  const applyPayload = useCallback((data: unknown) => {
    const payload = data as SsePayload;
    initialLoadDone.current = true;
    setFetching(false);
    if (payload.items.length === 0 && payload.totalCount === 0) {
      setState({ kind: "empty" });
    } else {
      setState({
        kind: "data",
        items: payload.items,
        totalCount: payload.totalCount,
      });
    }
  }, []);

  const subscribe = useCallback(
    (currentPage: number, currentPageSize: number, currentFilters: TaskListFilters) => {
      if (clientRef.current !== null) {
        clientRef.current.close();
      }

      if (!initialLoadDone.current) {
        setState({ kind: "loading" });
      }
      setFetching(true);

      const parameters: Record<string, string | number | string[]> = {
        page: currentPage,
        pageSize: currentPageSize,
      };
      if (currentFilters.anchorTextFilter !== "") {
        parameters.anchorTextFilter = currentFilters.anchorTextFilter;
      }
      if (currentFilters.targetUrlFilter !== "") {
        parameters.targetUrlFilter = currentFilters.targetUrlFilter;
      }
      if (currentFilters.deactivated !== undefined) {
        parameters.deactivated = String(currentFilters.deactivated);
      }
      if (currentFilters.regionFilter.length > 0) {
        parameters.regionFilter = currentFilters.regionFilter;
      }
      if (currentFilters.statusFilter !== undefined) {
        parameters.statusFilter = currentFilters.statusFilter;
      }
      if (currentFilters.taskTypeFilter !== undefined) {
        parameters.taskTypeFilter = currentFilters.taskTypeFilter;
      }

      const client = createSseClient({
        url: "/api/tasks/subscribe",
        params: parameters,
        events: {
          initial: applyPayload,
          state: applyPayload,
        },
        onError() {
          setFetching(false);
          setState({ kind: "error" });
        },
      });

      clientRef.current = client;
    },
    [applyPayload],
  );

  useEffect(() => {
    subscribe(page, pageSize, filters);
    return () => {
      if (clientRef.current !== null) {
        clientRef.current.close();
      }
      clientRef.current = null;
    };
  }, [subscribe, page, pageSize, filters]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const setPageSize = useCallback((newPageSize: number) => {
    setPageSizeState(newPageSize);
    setPageState(1);
  }, []);

  return { state, fetching, page, pageSize, setPage, setPageSize };
}
